import React, { useEffect } from 'react';
import classNames from 'classnames';
import Blanket from '@atlaskit/blanket';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import { ExitingPersistence, SlideIn } from '@atlaskit/motion';
import { ENABLE_DESIGN_ITERATION_1 } from '@css-xp/ppc-constants';
import { useFeatureToggle } from '../../..';
import { AllowedSidebarMap, useGlobalEscapeKeyPress, useSidebar } from '../../../hooks';
import { InfoPanel } from '../../../info-panel';
import './sidebar.scss';
import { GloballyAllowedSidebar } from '@atlassian/cssxp-utils-generic';
// eslint-disable-next-line workspaces/no-relative-imports, workspaces/require-dependency
import ServiceCreditPane from '../../../../account-subscriptions/cloud-site/src/tabs/invoices/service-credit-pane/service-credit-pane';
import { Animation } from '../../../constants';
import SidebarV2 from './sidebarv2';
import { BlanketWrapper } from './sidebar.styles';

// Globally accessible sidebars (ones that are enabled on any page)
const globalAllowlistSidebarMap: AllowedSidebarMap = {
  [GloballyAllowedSidebar.BILLING_SERVICE_CREDIT_FORM]: ServiceCreditPane,
};

const Sidebar = () => {
  const [isEnabled] = useFeatureToggle();
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);

  const togglePageScrolling = () => {
    const openSidebars = document.getElementsByClassName('sidebar-open');
    const isV2Page = document.getElementsByClassName('app-page-v2').length > 0;
    const isApprovalDeskPage = document.getElementsByClassName('apd-app-page').length > 0;

    // Note: This `isV2Page` check is not mandatory as it does not impact the
    // older pages. However, added it to avoid any future issues.
    // Can be removed if all the older pages are removed.
    /* istanbul ignore next */
    if (isV2Page || isApprovalDeskPage) {
      if (openSidebars?.length > 0) {
        document.body.style.overflow = 'hidden';
        Array.from(openSidebars).forEach((sidebar) => {
          sidebar.classList.add('sidebar-extra');
        });
      } else {
        document.body.style.overflow = '';
      }
    }
  };

  useEffect(() => {
    if (!isDesignIteration1Enabled) {
      togglePageScrolling();
    }
  });

  const { isSidebarOpen, payload, allowedSidebars, sidebarType, sidebarOptions, closeSidebar } = useSidebar();

  useGlobalEscapeKeyPress(closeSidebar);

  const allowedSidebarMap: AllowedSidebarMap = {
    ...globalAllowlistSidebarMap,
    ...allowedSidebars,
  };

  const Pane = allowedSidebarMap[sidebarType ?? ''];

  if (isDesignIteration1Enabled) {
    return (
      <>
        {isSidebarOpen && (
          <BlanketWrapper>
            <Blanket testId="overlay" isTinted={false} onBlanketClicked={closeSidebar} />
          </BlanketWrapper>
        )}
        <SidebarV2
          isSidebarOpen={isSidebarOpen}
          closeSidebar={closeSidebar}
          Pane={Pane}
          payload={payload}
          sidebarOptions={sidebarOptions}
        />
      </>
    );
  }

  return (
    <>
      {isSidebarOpen && sidebarOptions?.isOverlay && (
        <Blanket testId="overlay" isTinted onBlanketClicked={closeSidebar} />
      )}

      <ExitingPersistence>
        <div
          data-testid="sidebar-container"
          className={classNames('sidebar-container', sidebarOptions?.className, {
            'sidebar-open': isSidebarOpen,
            'sidebar-overlay': sidebarOptions?.isOverlay ?? false,
          })}
          style={{
            width: isSidebarOpen ? sidebarOptions?.sidebarWidth : undefined,
            // replicates the page layout without the additional stylings
            gridArea: 'right-sidebar',
          }}
        >
          {isSidebarOpen && (
            <SlideIn enterFrom={'right'} fade={'inout'} duration={Animation.DEFAULT_DURATION}>
              {(props) => (
                // `props` has classname in it, so using inline style to avoid conflict
                <section {...props} style={{ width: '100%' }}>
                  <div className="pane-sidebar" data-testid="sidebar-content">
                    <div className="close" onClick={closeSidebar} data-testid="sidebar-close">
                      <CrossIcon size="medium" label="Close sidebar" />
                    </div>
                    {Pane ? (
                      <Pane data-testid="pane-test" {...payload} />
                    ) : (
                      <>
                        <InfoPanel
                          type="warning"
                          title="The pane has not been setup yet"
                          message="Please check the application and setup the sidebar panes"
                        />
                      </>
                    )}
                  </div>
                </section>
              )}
            </SlideIn>
          )}
        </div>
      </ExitingPersistence>
    </>
  );
};

export default Sidebar;
